import {FlowProperty, FlowValidationError} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {
    CloudCategory,
    CloudProperty,
    PropertyWidgetName,
} from '@mintdata-wsp/common/state/ui/visualDesigner/VisualDesignerState';

import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {PropertyInlineLabel} from '@mintdata-wsp/wsp/ui/views/visualDesigner/flow/panels/properties/widgets/common/PropertyInlineLabel';
import {
    CheckboxWidget,
    DropdownWidget,
    EditableMultiSuggestWidget,
    EditableSuggestWidget,
    EmailWidget,
    FrameColumnNamesWidget,
    FrameColumnNameWidget,
    HostnameWidget,
    IPropertyVisibilityMap,
    IWidgetComponentProps,
    JavascriptWidget,
    ListWidget,
    MapWidget,
    MultiSuggestWidget,
    NumericInputWidget,
    PasswordWidget,
    PortWidget,
    SuggestWidget,
    TextInputWidget,
    MultilineTextInputWidget,
    UnsupportedWidget,
    URLWidget,
    WidgetComponent,
} from '@mintdata-wsp/wsp/ui/views/visualDesigner/flow/panels/properties/widgets/FlowPropertiesWidgets';
import * as React from 'react';
import _sortBy from 'lodash/sortBy';

const checkboxStyles = require('@mintdata/mint-kit/impl/properties/components/checkbox/Checkbox.less');

export abstract class FlowPropertiesUtils
{
    public static getWidgetComponent(definition:CloudProperty):WidgetComponent<IWidgetComponentProps>
    {
        const widget = controllers.ui.flowPropertiesPanel.getWidgetName(definition);

        switch( widget )
        {
            case PropertyWidgetName.TextInput:
                return TextInputWidget;
            case PropertyWidgetName.Multiline:
                return MultilineTextInputWidget;
            case PropertyWidgetName.URL:
                return URLWidget;
            case PropertyWidgetName.Hostname:
                return HostnameWidget;
            case PropertyWidgetName.Port:
                return PortWidget;
            case PropertyWidgetName.Email:
                return EmailWidget;
            case PropertyWidgetName.NumericInput:
                return NumericInputWidget;
            case PropertyWidgetName.Dropdown:
                return DropdownWidget;
            case PropertyWidgetName.Suggest:
                return SuggestWidget;
            case PropertyWidgetName.EditableSuggest:
                return EditableSuggestWidget;
            case PropertyWidgetName.MultiSuggest:
                return MultiSuggestWidget;
            case PropertyWidgetName.EditableMultiSuggest:
                return EditableMultiSuggestWidget;
            case PropertyWidgetName.Checkbox:
                return CheckboxWidget;
            case PropertyWidgetName.Javascript:
                return JavascriptWidget;
            case PropertyWidgetName.Password:
                return PasswordWidget;
            case PropertyWidgetName.List:
                return ListWidget;
            case PropertyWidgetName.Map:
                return MapWidget;
            case PropertyWidgetName.FrameColumnName:
                return FrameColumnNameWidget;
            case PropertyWidgetName.FrameColumnNames:
                return FrameColumnNamesWidget;
            default:
            {
                return (props:IWidgetComponentProps) =>
                {
                    return (
                        <UnsupportedWidget
                            {...props}
                            reason={`Unknown property type ${definition.type}`}
                        />
                    );
                };
            }
        }
    }

    public static getOrderedCategories(categories:CloudCategory[]):CloudCategory[]
    {
        return _sortBy(categories, ({order}) => order);
    }

    public static getOrderedProps(props:FlowProperty[]):FlowProperty[]
    {
        return _sortBy(props, ({definition}) => definition.index);
    }

    public static getPropVisibilityMap(props:FlowProperty[]):IPropertyVisibilityMap
    {
        return props.reduce((result:IPropertyVisibilityMap, prop:FlowProperty) =>
        {
            const {definition} = prop;
            const {name: propName} = definition;

            result[propName] = FlowPropertiesUtils._isPropVisible(prop, props);

            return result;
        }, {});
    }

    private static _isPropVisible(prop:FlowProperty, props:FlowProperty[]):boolean
    {
        const {definition} = prop;
        const {visibility} = definition;

        if( !visibility )
        {
            return true;
        }

        const args = props.map(({definition}) => definition.name);
        const values = props.map(({value}) => value);

        try
        {
            const fn = new Function(...args, `return ${visibility};`);
            return fn(...values);
        }
        catch( err )
        {
            console.warn(`Warning: visibility expr of ${definition.name} property is not valid`, err);
            return true;
        }
    }

    public static getPropError(errors:FlowValidationError[], propName:string):FlowValidationError
    {
        return errors.find(({flowPropertyName}) => flowPropertyName === propName);
    }

    public static getPropInlineLabel(prop:FlowProperty)
    {
        const {definition} = prop;
        const {label, description} = definition;
        const widget = controllers.ui.flowPropertiesPanel.getWidgetName(definition);

        let labelProp:string | JSX.Element = label;

        if( widget === PropertyWidgetName.Checkbox )
        {
            labelProp = <span className={checkboxStyles.inlineLabel}>{label}</span>;
        }

        return (
            <PropertyInlineLabel
                label={labelProp}
                description={description}
            />
        );
    }
}
